import { useEffect, useState } from "react";
import axios from "axios";
import {
  Container,
  Stack,
  Typography,
  Box,
  IconButton,
  Button,
} from "@mui/material";
import {
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

import {
  useGetClassrooms,
  useAddClassroom,
  useUpdateClassroom,
  useDeleteClassrooms,
} from "api/management/classrooms";
import { DataGrid, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";

import { ClassroomDialog, IClassroom } from "./parts";
import {
  ConfirmationDialog,
  MessageDialog,
  ProgressBackdropDialog,
} from "components/parts/dialogs";
import { useI18n } from "i18n";

const Page = () => {
  const { t } = useI18n();
  const { loading: getLoading, service: getClassrooms } = useGetClassrooms();
  const { loading: addLoading, service: addClassroom } = useAddClassroom();
  const { loading: updateLoading, service: updateClassroom } =
    useUpdateClassroom();
  const { loading: deleteLoading, service: deleteClassrooms } =
    useDeleteClassrooms();

  const [classrooms, setClassrooms] = useState<IClassroom[]>([]);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openNoticeDialog, setOpenNoticeDialog] = useState(false);
  const [noticeMessage, setNoticeMessage] = useState<string>("");

  const [editData, setEditData] = useState<IClassroom | null>(null);
  const [editType, setEditType] = useState<"edit" | "add" | "none">("none");
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);

  const handleEditClick = (row) => {
    setEditType("edit");
    setEditData(row);
    setOpenEditDialog(true);
  };

  const handleAddClick = () => {
    setEditType("add");
    setEditData(null);
    setOpenEditDialog(true);
  };

  const handleDeleteClick = () => {
    if (rowSelectionModel.length === 0) {
      setNoticeMessage(t("classrooms.selectToDelete"));
      setOpenNoticeDialog(true);
      return;
    }

    setOpenDeleteDialog(true);
  };

  const columns: GridColDef[] = [
    { field: "id", headerName: t("field.id"), width: 90, hideable: false },
    {
      field: "roomNumber",
      headerName: t("field.roomNumber"),
      width: 150,
      editable: false,
    },
    {
      field: "name",
      headerName: t("field.roomName"),
      width: 150,
      editable: false,
    },
    {
      field: "actions",
      headerName: t("common.actions"),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <IconButton color="primary" onClick={() => handleEditClick(params.row)}>
          <EditIcon />
        </IconButton>
      ),
    },
  ];

  useEffect(() => {
    getClassrooms()
      .then((response) => {
        const { data: result } = response;
        return result;
      })
      .then((result) => {
        const { data } = result;
        setClassrooms(data);
      })
      .catch((error) => {
        if (axios.isCancel(error)) return;
        console.log(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center">
          <Typography
            variant="h1"
            gutterBottom
            sx={{ fontSize: "24px", fontWeight: "bold", mb: 4 }}
          >
            {t("classrooms.title")}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{ mr: 1 }}
          >
            {t("common.add")}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
          >
            {t("common.delete")}
          </Button>
        </Stack>
        <Box sx={{ height: 500, width: "100%" }}>
          <DataGrid
            loading={getLoading}
            rows={classrooms}
            rowCount={classrooms.length}
            columns={columns}
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setRowSelectionModel(newRowSelectionModel);
            }}
            rowSelectionModel={rowSelectionModel}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
              columns: {
                columnVisibilityModel: {
                  id: false,
                },
              },
            }}
            sx={{ minHeight: 400 }}
            pageSizeOptions={[10, 20, 50]}
            checkboxSelection
            disableRowSelectionOnClick
          />
        </Box>
      </Container>
      <ClassroomDialog
        open={openEditDialog}
        editType={editType}
        editData={editData}
        handleClose={() => {
          setEditData(null);
          setOpenEditDialog(false);
        }}
        handleSubmit={async (data) => {
          let result = false;
          try {
            if (editType === "add") {
              // 追加処理
              const resultData = await addClassroom(data).then((response) => {
                const { data: result } = response;
                return result["data"];
              });

              setClassrooms([...classrooms, resultData]);
            } else if (editType === "edit") {
              // 編集処理
              const resultData = await updateClassroom(data).then(
                (response) => {
                  const { data: result } = response;
                  return result["data"];
                }
              );
              setClassrooms((prevRows) => {
                return prevRows.map((row) => {
                  const prevId = row["id"];
                  return prevId === resultData["id"]
                    ? {
                        ...row,
                        name: resultData["name"],
                        roomNumber: resultData["roomNumber"],
                      }
                    : row;
                });
              });
            }
            setOpenEditDialog(false);
            result = true;
          } catch (error) {
            console.log(error);
            result = false;
          } finally {
            return result;
          }
        }}
      />

      <ConfirmationDialog
        title={t("classrooms.deleteTitle")}
        message={t("classrooms.deleteMessage")}
        negativeText={t("common.cancel")}
        positiveText={t("common.delete")}
        open={openDeleteDialog}
        handleClose={() => setOpenDeleteDialog(false)}
        handleSubmit={() => {
          const idArray = rowSelectionModel;
          deleteClassrooms(idArray)
            .then(() => {
              // 選択された行のIDに基づいて行を一括削除
              setClassrooms(
                classrooms.filter((row) => !rowSelectionModel.includes(row.id))
              );
              setRowSelectionModel([]); // 選択状態をクリア
              setOpenDeleteDialog(false);
            })
            .catch((error) => {
              console.log(error);
            });
        }}
      />
      <MessageDialog
        title={t("common.notice")}
        message={noticeMessage}
        open={openNoticeDialog}
        actionText={t("common.confirm")}
        handleClose={() => {
          setOpenNoticeDialog(false);
        }}
      />
      <ProgressBackdropDialog
        open={addLoading || updateLoading || deleteLoading}
      />
    </>
  );
};

export default Page;
